/**
 * The Sortiment flavour filter. Replaces the second Isotope instance -- the one
 * on `.grid` that had nothing to do with the galleries and was the reason
 * isotope.pkgd stayed on the page after the galleries stopped using it.
 *
 * Isotope was laying the 24 cards out with absolute positioning and animating
 * them between filters. A CSS grid holds the same layout without script, so all
 * that is left here is which cards are `hidden` -- and the grid reflows itself.
 *
 * Opt in from markup: `data-filter-group` on the button row,
 * `data-filter-target` naming the grid, `data-filter` on each button matching a
 * `data-flavour` on the cards. An empty `data-filter` is "all".
 *
 * THE URL FRAGMENT IS PART OF THE INTERFACE. The home page's Sortiment teaser
 * links to /sortiment/#schokoladig, and nothing here used to read it: the page
 * opened unfiltered, and because no element carries that id the browser did not
 * scroll anywhere either, so the link did nothing at all. The fragment is a
 * filter value, not an anchor.
 *
 * The values stay GERMAN in both languages -- `data-filter="schokoladig"` with
 * the visible label translated to "chocolate" -- because the value is a key.
 * One behaviour covers both trees and the English home page's links work
 * unchanged.
 */

import { replayStagger, revealTargetsWithin, settleReveal } from './reveal.js'
import { scribble } from './scribble.js'

const SELECTOR = '[data-filter-group]'

const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches

function setup (group, signal) {
  const grid = document.querySelector(group.dataset.filterTarget || '')
  const buttons = Array.from(group.querySelectorAll('[data-filter]'))
  if (!grid || buttons.length === 0) return

  /* The result, for a screen reader and for everyone else. Pressing a filter
     button changes eleven cards further down the page and says nothing about
     it: sighted users see the grid reflow, a screen-reader user gets silence
     and a button that is now pressed -- a11y audit finding 7.1.

     The region is rendered empty by 07_tortensorteFilter and only written to
     here. It is never created on the fly: a live region has to be in the
     document before it changes, or the first change goes unannounced. */
  const status = group.querySelector('[data-filter-count]')
  const template = status ? status.dataset.filterCountTemplate || '' : ''

  /* WRITTEN ON EVERY STATE, INCLUDING "Alle" AND INCLUDING ON LOAD. Ben's call
     of 2026-09-22, reversing what this used to do.

     It used to blank the line for "all" and only write at all when the change
     was one the visitor had asked for, on the argument that "24 von 24 Sorten"
     tells nobody anything and that a live region written during start-up is
     announced out of context. The first half was wrong about sighted readers:
     the count is the only thing on the page that says how many Sorten there
     are, and it appeared only once you had filtered them away.

     The second half was a real concern and it is answered by how live regions
     work rather than by staying silent: a role="status" that ALREADY HOLDS
     TEXT when it is registered is not announced -- only later changes are. So
     writing the initial count costs no spurious announcement, and every change
     after it is a change the visitor made. */
  const announce = (value) => {
    if (!status) return
    const total = grid.children.length
    let count = 0
    for (const item of grid.children) if (!item.hidden) count++
    status.textContent = template
      .replace('{count}', String(count))
      .replace('{total}', String(total))
  }

  /* `animate` is false exactly twice: on load, and for a deep link that
     arrives with the page. It was called `speak` when it also gated the count,
     which it no longer does -- the count is written on every state now, and
     the flag is left with the one job it still has. */
  const apply = (value, animate) => {
    for (const item of grid.children) {
      item.hidden = value !== '' && item.dataset.flavour !== value
    }
    buttons.forEach((button) => {
      const current = button.dataset.filter === value
      button.classList.toggle('is-current', current)
      button.setAttribute('aria-pressed', String(current))
      /* "Alle" carries no mark -- Ben, 2026-09-22. An empty data-filter is
         "all", and with the load state unmarked too, the first loop a visitor
         ever sees is drawn by their own click. The test stays here rather than
         inside scribble.js, which knows nothing about filters. */
      scribble(button, current && button.dataset.filter !== '', { animate })
    })
    announce(value)
  }

  /* Only a fragment that names a button filters. A card's id is landed on in
     applyFromHash(); anything else -- another anchor on the page, a stale
     link, junk -- is left to the browser. */
  const known = new Set(buttons.map((button) => button.dataset.filter))

  /* Where a deep link lands: the SECTION, not the row of buttons. Arriving on
     the buttons drops the visitor into the middle of a block with its heading
     already scrolled off -- they see eleven cards and no "Unser Sortiment" to
     tell them what they are looking at.

     Resolved from the markup rather than named by a class: the nearest ancestor
     that carries a heading is the block the heading belongs to, whatever the
     ContentBlocks layout happens to call its wrappers. Falls back to the group,
     which is where it used to go. */
  const section = (() => {
    for (let node = group.parentElement; node && node !== document.body; node = node.parentElement) {
      if (node.querySelector('h1, h2, h3')) return node
    }
    return group
  })()
  section.classList.add('is-filter-section')

  /* PUT THE WHOLE SECTION DOWN, WITHOUT TRAVEL, FOR A DEEP LINK. Someone who
     followed /sortiment/#schokoladig is being PUT here: a section that fades
     up from below after they arrive animates something they did not scroll to.

     THE BUTTON IS THE OTHER CASE AND IT IS NOT THIS ONE. D4 said on
     2026-09-20 that everything counts as revealed once a filter has run, and
     Ben reversed that on 2026-09-21 after seeing it: pressing a flavour
     rearranges the grid around a different eleven cards, and a set of cards
     that has never been in this arrangement before has not "already arrived"
     in any sense the reader would recognise. The click handler replays the
     wave instead -- src/js/reveal.js, replayStagger(). The deep link keeps
     this behaviour, because it is the one case where the reader did not ask
     for the grid to do anything.

     The walk used to go UPWARDS only, and its reason still holds: until
     reveal.js marks a section `is-revealed` it carries translateY(100px), a
     transform moves an element's rect without changing the layout, and
     scrollIntoView aimed 100px low -- traced at 9px off after 500ms and 100px
     off after 1200ms, with the scroll position and the document height
     constant throughout. What is new is the other direction. The cards are
     reveal targets now, which makes them DESCENDANTS of the section, and an
     ancestors-only walk would have landed the reader and then cascaded eleven
     cards at them.

     The grid's own children are added by name as well as by selector: a
     filtered-out card is `hidden`, and a hidden element never intersects, so
     the observer alone would leave the ones below the fold unrevealed.

     Switching the transition off is the whole point: adding the class alone
     STARTS a three-quarter-second animation from translateY, which is why the
     first attempt at this landed correctly on a slow load and a hundred pixels
     high on a fast one. The destination has to arrive, not animate. The inline
     delay goes with it -- reveal.js writes one per member, and a settled card
     must not be sitting on a queue. reveal.js's settleReveal() does both, and
     owns the class name and the mechanics that go with it; this module only
     says WHICH elements.

     Which is the correction this block needed: it used to carry its own
     selector for "everything that arrives by animating", and that selector had
     drifted from the stylesheet's. */
  const settle = () => {
    const nodes = new Set()
    for (let node = section; node; node = node.parentElement) {
      if (node.dataset && node.dataset.reveal !== undefined) nodes.add(node)
    }
    for (const node of revealTargetsWithin(section)) nodes.add(node)
    /* The grid's own children by name as well, because the grid is only
       USUALLY inside the section: what makes an element the section is a
       heading, and what makes the grid the grid is `data-filter-target`. */
    for (const item of grid.children) nodes.add(item)
    settleReveal(nodes)
  }

  /* A filter that has been applied out of sight has not been applied as far as
     the visitor is concerned: the Sortiment teaser sits on the home page, and
     the page it links to opens on a 70vh photograph. So the fragment brings the
     row of buttons into view as well -- the BUTTONS rather than the grid,
     because seeing which one is pressed is what explains why 11 of 24 cards are
     showing.

     Instant on load, smooth when the fragment changes while the page is already
     open. An anchor jumps, and a half-second animated descent through a hero
     the visitor never asked to look at is not an improvement on that; but once
     they are on the page, the same move reads as a transition and is worth
     animating. Clicking a button does not scroll at all -- it goes through
     replaceState, which fires no hashchange. */
  const land = (target, smooth) => {
    const scroll = () => {
      target.scrollIntoView({ behavior: smooth && !reduced() ? 'smooth' : 'auto' })
    }
    if (smooth || document.readyState === 'complete') scroll()
    else window.addEventListener('load', scroll, { once: true, signal })
  }

  const applyFromHash = (smooth) => {
    const wanted = decodeURIComponent(window.location.hash.slice(1))
    if (!wanted) return

    /* A fragment that names a CARD is where a torte's own URL lands: B5/F7
       301s /manufakturtorte to /sortiment/#manufakturtorte. Left to the
       browser, the jump aims at the card while it still carries the reveal's
       translateY, and the cake's name ends above the viewport once the card
       rises -- measured at -108px in Chromium and Gecko. So it is put down
       and landed on like the section below. The filter is left alone: a
       redirect arrives on "Alle", where every card is showing. */
    const card = Array.from(grid.children).find((item) => item.id === wanted)
    if (card) {
      settle()
      land(card, smooth)
      return
    }

    if (!known.has(wanted)) return

    /* Filter now, scroll later. The cards must not be seen unfiltered even for
       a frame, but a scroll computed before the images below have sized
       themselves lands in the wrong place -- measured: the row came to rest 100
       px too high, under the sticky bar, because the page grew underneath it
       after the module ran. */
    /* `smooth` is false exactly once, on load, and doubles as "do not
       animate": the count is written either way -- see announce() -- but a
       loop drawn around a button the visitor has not looked at yet is the
       same mistake as a section that fades up after they were put on it. */
    apply(wanted, smooth)

    /* Before measuring anything: the destination has to be where it will be,
       and the cards the reader was sent to must not animate at them. */
    settle()
    land(section, smooth)
  }

  group.addEventListener('click', (event) => {
    const button = event.target.closest('[data-filter]')
    if (!button) return
    apply(button.dataset.filter, true)
    /* AFTER the cards have moved, not before: the wave is sorted by painted
       position, and the grid has just reflowed around a different set. This
       also answers what happens to a card that was hidden while it was still
       below the fold and has never been revealed -- it is in the replay, so
       it arrives with the rest instead of coming back blank. */
    replayStagger(grid)

    /* The URL follows the filter, so what is on screen is what gets shared and
       what comes back on reload. replaceState rather than assigning
       location.hash: assigning pushes a history entry per click, and the back
       button would then walk backwards through the filters instead of leaving
       the page. It also fires hashchange, which would re-enter apply(). */
    window.history.replaceState(null, '', button.dataset.filter
      ? '#' + button.dataset.filter
      : window.location.pathname + window.location.search)
  })

  /* A link to this page from this page -- the same URL with a different
     fragment -- does not reload, so the filter has to follow the fragment. */
  window.addEventListener('hashchange', () => applyFromHash(true), { signal })

  /* Whichever button the markup shipped as pressed, so the rendered state and
     the state this module believes in cannot start out disagreeing; then the
     fragment, which outranks it because it is the more specific request. */
  const pressed = buttons.find((button) => button.getAttribute('aria-pressed') === 'true')
  apply(pressed ? pressed.dataset.filter : '', false)
  applyFromHash(false)
}

export function initFilter (root = document) {
  /* The two window-level listeners are why this needs a teardown at all --
     everything else is bound to elements inside the container Swup replaces
     and goes with it. `hashchange` in particular would otherwise stack up one
     handler per navigation, each still filtering a detached list. */
  const controller = new AbortController()
  root.querySelectorAll(SELECTOR).forEach((node) => setup(node, controller.signal))
  return () => controller.abort()
}
